import React, { Component } from 'react';

import { getCloudfrontProxyUrl, isCloudfrontAssetUrl } from '../../Lib/cloudfrontAsset';
import { logClientEvent } from '../../Lib/clientEventLog';

const FALLBACK_TIMEOUT_MS = 2000;

const getSourceUrl = (props) => props.src || props.imageUrl || '';

class CloudfrontAssetBase extends Component {
    constructor(props) {
        super(props);
        const sourceUrl = getSourceUrl(props);
        this.state = {
            displayUrl: sourceUrl,
            usingProxy: false,
        };
        this.originalTimer = null;
        this.originalImage = null;
        this.proxyImage = null;
        this.handleElementError = this.handleElementError.bind(this);
    }

    componentDidMount() {
        this.startOriginalPreload(getSourceUrl(this.props));
    }

    componentDidUpdate(prevProps) {
        const prevSourceUrl = getSourceUrl(prevProps);
        const sourceUrl = getSourceUrl(this.props);
        if (prevSourceUrl !== sourceUrl) {
            this.clearOriginalPreload();
            this.proxyImage = null;
            this.setState({
                displayUrl: sourceUrl,
                usingProxy: false,
            }, () => {
                this.startOriginalPreload(sourceUrl);
            });
        }
    }

    componentWillUnmount() {
        this.clearOriginalPreload();
        this.proxyImage = null;
    }

    clearOriginalPreload() {
        if (this.originalTimer) {
            clearTimeout(this.originalTimer);
            this.originalTimer = null;
        }
        if (this.originalImage) {
            this.originalImage.onload = null;
            this.originalImage.onerror = null;
            this.originalImage = null;
        }
    }

    logEvent(eventName, details) {
        const sourceUrl = getSourceUrl(this.props);
        logClientEvent(eventName, Object.assign(
            {},
            this.props.logContext || {},
            {
                src: sourceUrl,
                displayUrl: this.state.displayUrl,
            },
            details || {}
        ));
    }

    startOriginalPreload(sourceUrl) {
        if (!isCloudfrontAssetUrl(sourceUrl) || typeof Image === 'undefined') return;

        let completed = false;
        const image = new Image();
        this.originalImage = image;

        const complete = (success, reason) => {
            if (completed) return;
            completed = true;
            this.clearOriginalPreload();
            if (!success) this.applyProxyFallback(reason);
        };

        image.onload = () => complete(true);
        image.onerror = () => complete(false, 'preload_error');
        this.originalTimer = setTimeout(() => {
            complete(false, 'preload_timeout');
        }, FALLBACK_TIMEOUT_MS);
        image.src = sourceUrl;
    }

    applyProxyFallback(reason) {
        const sourceUrl = getSourceUrl(this.props);
        if (!isCloudfrontAssetUrl(sourceUrl) || this.state.usingProxy) return;

        const proxyUrl = getCloudfrontProxyUrl(sourceUrl);
        this.logEvent('cloudfront_asset_fallback_start', {
            severity: 'warn',
            reason,
            proxyUrl,
        });

        this.setState({
            displayUrl: proxyUrl,
            usingProxy: true,
        }, () => {
            this.preloadProxy(proxyUrl, reason);
        });
    }

    preloadProxy(proxyUrl, reason) {
        if (typeof Image === 'undefined') return;
        const image = new Image();
        this.proxyImage = image;
        image.onload = () => {
            this.logEvent('cloudfront_asset_fallback_success', {
                severity: 'info',
                reason,
                proxyUrl,
            });
        };
        image.onerror = () => {
            this.logEvent('cloudfront_asset_fallback_fail', {
                severity: 'error',
                reason,
                proxyUrl,
            });
        };
        image.src = proxyUrl;
    }

    handleElementError(event) {
        if (this.state.usingProxy) {
            this.logEvent('cloudfront_asset_render_fail', {
                severity: 'error',
                proxyUrl: this.state.displayUrl,
            });
        } else {
            this.applyProxyFallback('render_error');
        }

        if (this.props.onError) this.props.onError(event);
    }
}

export class CloudfrontImage extends CloudfrontAssetBase {
    render() {
        const {
            src,
            imageUrl,
            logContext,
            onError,
            ...imageProps
        } = this.props;
        return (
            <img
                {...imageProps}
                src={this.state.displayUrl || src || imageUrl}
                onError={this.handleElementError}
            />
        );
    }
}

export class CloudfrontBackground extends CloudfrontAssetBase {
    render() {
        const {
            children,
            className,
            imageUrl,
            logContext,
            onClick,
            style,
        } = this.props;
        const nextStyle = Object.assign(
            {},
            style || {},
            this.state.displayUrl ? { backgroundImage: "url('" + this.state.displayUrl + "')" } : {}
        );

        return (
            <div className={className} style={nextStyle} onClick={onClick}>
                {children}
            </div>
        );
    }
}
